import { useCallback } from 'react'
import moment from 'moment-timezone'

const getWeekOfMonth = (date) => {
  const momentDate = moment(date)
  const firstDayOfMonth = momentDate.clone().startOf('month')
  const dayOfMonth = momentDate.date()
  const firstDayOfWeek = firstDayOfMonth.day()
  return Math.ceil((dayOfMonth + firstDayOfWeek) / 7)
}

const findNthWeekdayInMonth = (year, month, dayOfWeek, weekNumber) => {
  const firstDay = moment([year, month, 1])
  const firstDayOfWeek = firstDay.day()
  const daysToAdd = (dayOfWeek - firstDayOfWeek + 7) % 7
  const targetDate = firstDay.clone().add(daysToAdd, 'days')
  targetDate.add(weekNumber - 1, 'weeks')
  return targetDate
}

export const useRecurringPatterns = (
  convertDateAndSlotToUTC,
  sowSessionDuration,
) => {
  const generateRecurringDates = useCallback(
    (startDate, pattern, totalSessions, timeSlot) => {
      const dates = []
      const start = moment(startDate)
      const dayOfWeek = start.day()
      const weekOfMonth = getWeekOfMonth(startDate)
      let currentDate = start.clone()

      for (let i = 0; i < totalSessions; i++) {
        if (i === 0) {
          const firstDateStr = currentDate.format('YYYY-MM-DD')
          const { start: slotStart, end: slotEnd } = convertDateAndSlotToUTC(
            firstDateStr,
            timeSlot.start,
            timeSlot.end,
          )

          dates.push({
            date: firstDateStr,
            slotStart: slotStart.toISOString(),
            slotEnd: slotEnd
              ? slotEnd.toISOString()
              : slotStart
                  .clone()
                  .add(sowSessionDuration || 0, 'minutes')
                  .toISOString(),
          })
        } else {
          let nextDate

          if (pattern === 'weekly') {
            nextDate = currentDate.clone().add(1, 'week')
          } else if (pattern === 'alternate' || pattern === 'bi-weekly') {
            nextDate = currentDate.clone().add(2, 'weeks')
          } else if (pattern === 'every-3-weeks') {
            nextDate = currentDate.clone().add(3, 'weeks')
          } else if (
            pattern.startsWith('every-') &&
            pattern.endsWith('-weeks')
          ) {
            const weeksMatch = pattern.match(/every-(\d+)-weeks/)
            if (weeksMatch) {
              const weeks = parseInt(weeksMatch[1], 10)
              nextDate = currentDate.clone().add(weeks, 'weeks')
            }
          } else if (pattern === 'monthly') {
            nextDate = currentDate.clone().add(1, 'month')
            const nextMonth = nextDate.month()
            const nextYear = nextDate.year()
            nextDate = findNthWeekdayInMonth(
              nextYear,
              nextMonth,
              dayOfWeek,
              weekOfMonth,
            )
          } else if (pattern === 'bi-monthly') {
            nextDate = currentDate.clone().add(2, 'months')
            const nextMonth = nextDate.month()
            const nextYear = nextDate.year()
            nextDate = findNthWeekdayInMonth(
              nextYear,
              nextMonth,
              dayOfWeek,
              weekOfMonth,
            )
          } else if (pattern === 'quarterly') {
            nextDate = currentDate.clone().add(3, 'months')
            const nextMonth = nextDate.month()
            const nextYear = nextDate.year()
            nextDate = findNthWeekdayInMonth(
              nextYear,
              nextMonth,
              dayOfWeek,
              weekOfMonth,
            )
          } else if (
            pattern.startsWith('every-') &&
            pattern.endsWith('-months')
          ) {
            const monthsMatch = pattern.match(/every-(\d+)-months/)
            if (monthsMatch) {
              const months = parseInt(monthsMatch[1], 10)
              nextDate = currentDate.clone().add(months, 'months')
              const nextMonth = nextDate.month()
              const nextYear = nextDate.year()
              nextDate = findNthWeekdayInMonth(
                nextYear,
                nextMonth,
                dayOfWeek,
                weekOfMonth,
              )
            }
          }

          if (nextDate && nextDate.isValid()) {
            const nextDateStr = nextDate.format('YYYY-MM-DD')
            const { start: slotStart, end: slotEndFromConversion } =
              convertDateAndSlotToUTC(nextDateStr, timeSlot.start, timeSlot.end)

            const slotEnd = slotEndFromConversion
              ? slotEndFromConversion
              : slotStart.clone().add(sowSessionDuration || 0, 'minutes')

            dates.push({
              date: nextDate.format('YYYY-MM-DD'),
              slotStart: slotStart.toISOString(),
              slotEnd: slotEnd.toISOString(),
            })

            currentDate = nextDate
          }
        }
      }

      return dates
    },
    [convertDateAndSlotToUTC, sowSessionDuration],
  )

  return { generateRecurringDates }
}
